import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
  type ActionFunctionArgs,
  type MetaFunction,
  json,
  type LoaderFunctionArgs,
  redirect,
} from '@remix-run/node'
import { Form, useActionData, useLoaderData } from '@remix-run/react'
import { useState } from 'react'
import { z } from 'zod'
import { ContactService } from '#app/entities/contact/service.server.js'
import ContactoEmail from '#app/features/email/contactoEmail.tsx'
import { getMetaTags } from '#app/shared/lib/meta-tags.ts'
import {
  phoneSchema,
  postalCodeSchema,
} from '#app/shared/lib/user-validation.ts'
import { redirectWithToast } from '#app/shared/ui/feedback/toast/toast.server.ts'
import { Field, PhoneField, TextareaField } from '#app/shared/ui/fields.tsx'
import { Icon } from '#app/shared/ui/icons/Icon.tsx'
import { Button } from '#app/shared/ui/inputs/button/Button.tsx'
import { CheckboxTermsAndPrivacy } from '#app/shared/ui/inputs/CheckboxTermsAndPrivacy.js'
import Header from '#app/shared/ui/layout/Header.tsx'
import {
  Breadcrumb,
  breadcrumbHome,
} from '#app/shared/ui/navigation/Breadcrumb.tsx'
import { ContactTypeEnum } from '../../../../types/enums'

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: ContactoFormSchema,
  })
  if (submission.status !== 'success') {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }
  const contactType = formData.get('contactType') as string
  if (
    contactType !== ContactTypeEnum.CONTACT &&
    contactType !== ContactTypeEnum.FINANCE &&
    contactType !== ContactTypeEnum.BOOKING &&
    contactType !== ContactTypeEnum.SEARCH &&
    contactType !== ContactTypeEnum.APPRAISAL
  ) {
    return redirectWithToast('/', {
      title: 'Tipo de contacto no válido',
      description: 'El tipo de contacto no es válido',
      type: 'error',
    })
  }
  const { name, postalCode, email, fullPhone, message } = submission.value
  const contact = await ContactService.create({
    name,
    postalCode,
    email,
    phoneNumber: fullPhone,
    message,
    type: contactType ?? ContactTypeEnum.CONTACT,
  })

  if (contact.success) {
    try {
      await ContactoEmail({
        name,
        postalCode,
        email,
        fullPhone,
        message,
      })
    } catch (error) {
      console.error('Error sending contacto email:', error)
    }
    return redirect('/contacto-result')
  } else {
    return redirectWithToast('/', {
      title: 'Mensaje no enviado',
      description: 'Ha ocurrido un error al enviar el mensaje',
      type: 'error',
    })
  }
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const contactType = url.searchParams.get('contactType') ?? 'contact'

  return { contactType }
}

export const meta: MetaFunction<typeof loader> = ({ matches }) => {
  return getMetaTags({
    matches,
    title: 'Contacto | Gama Óptima',
    description:
      'Contacta con Gama Óptima en Barcelona. Llámanos al 931 318 618, escríbenos o visítanos en Ronda General Mitre 38-40. Estamos aquí para ayudarte.',
    url: 'https://gamaoptima.com/contacto',
  })
}

export const ContactoFormSchema = z.object({
  name: z.string().min(1, 'Por favor, introduce tu nombre'),
  postalCode: postalCodeSchema,
  email: z.string().email('Por favor, introduce un email válido'),
  phoneNumber: phoneSchema,
  fullPhone: z.string(),
  message: z
    .string()
    .min(10, 'Por favor, introduce un mensaje de al menos 10 caracteres'),
})
export default function Contacto() {
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const actionData = useActionData<typeof action>()
  const { contactType } = useLoaderData<typeof loader>()
  const [form, fields] = useForm({
    id: 'login-form',
    constraint: getZodConstraint(ContactoFormSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ContactoFormSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <>
      <Header isSticky={true} showBanner={true} />
      <section className="bg-neutral-25 pb-20">
        <div
          className={`
            mx-auto w-full max-w-[1150px] px-4

            sm:px-14
          `}
        >
          <Breadcrumb items={[breadcrumbHome, { label: 'Contacto' }]} />
          <div
            className={`
              mt-[18px] flex flex-col items-start justify-center gap-5 px-4

              lg:flex-row lg:gap-16 lg:px-0
            `}
          >
            {/* Form */}
            <div className={`w-full`}>
              <h3
                className={`
                  mb-7 pr-0 text-h3

                  lg:pr-24

                  xl:pr-36
                `}
              >
                Estamos aquí para ayudarte a tomar la mejor decisión. ¡No dudes
                en consultarnos!
              </h3>
              <Form className="space-y-7" method="post" {...getFormProps(form)}>
                <input type="hidden" name="contactType" value={contactType} />
                <Field
                  labelProps={{
                    htmlFor: fields.name.id,
                    children: 'Nombre',
                  }}
                  inputProps={{
                    ...getInputProps(fields.name, { type: 'text' }),
                    autoComplete: 'name',
                    placeholder: 'Escribe aquí tu nombre',
                    className: 'mt-2 rounded-lg',
                  }}
                  inputClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.name.errors}
                />
                <Field
                  labelProps={{
                    htmlFor: fields.postalCode.id,
                    children: 'Código postal',
                  }}
                  inputProps={{
                    ...getInputProps(fields.postalCode, { type: 'text' }),
                    autoComplete: 'postal code',
                    placeholder: 'Escribe aquí tu código postal',
                    className: 'mt-2 rounded-lg',
                  }}
                  inputClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.postalCode.errors}
                />
                <Field
                  labelProps={{
                    htmlFor: fields.email.id,
                    children: 'Email',
                  }}
                  inputProps={{
                    ...getInputProps(fields.email, { type: 'email' }),
                    autoComplete: 'email',
                    className: 'mt-2 rounded-lg',
                    placeholder: 'Escribe aquí tu email',
                  }}
                  inputClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.email.errors}
                />
                <PhoneField
                  labelProps={{
                    htmlFor: fields.phoneNumber.id,
                    children: 'Teléfono',
                  }}
                  inputProps={{
                    ...getInputProps(fields.phoneNumber, { type: 'number' }),
                    autoComplete: 'phone',
                    placeholder: 'Escribe aquí tu teléfono',
                    className: 'mt-2',
                  }}
                  fullPhone={fields.fullPhone.name}
                  errors={fields.phoneNumber.errors}
                />
                <TextareaField
                  labelProps={{
                    htmlFor: fields.message.id,
                    children: 'Tu mensaje',
                    className: 'text-subtitle1-bold',
                  }}
                  textareaProps={{
                    ...getInputProps(fields.message, { type: 'text' }),
                    autoComplete: 'message',
                    placeholder:
                      'Cuéntanos los detalles del coche que estas buscando',
                    className: 'mt-2 rounded-lg',
                    rows: 3,
                  }}
                  textareaClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.message.errors}
                />
                <CheckboxTermsAndPrivacy
                  isTermsAccepted={isTermsAccepted}
                  setIsTermsAccepted={setIsTermsAccepted}
                />
                <Button
                  type="submit"
                  className={`
                    px-6 py-5 text-base-bold text-white

                    disabled:opacity-20
                  `}
                  disabled={!isTermsAccepted}
                >
                  Enviar
                </Button>
              </Form>
            </div>
            {/* Contact Information */}
            <div
              className={`
                mt-6 w-full rounded-lg border border-neutral-75 bg-white p-8

                lg:ml-6 lg:mt-0 lg:w-2/5
              `}
            >
              <ul className="space-y-4 text-base text-neutral-700">
                <li className="flex items-start gap-1">
                  <Icon
                    name="basic/marker-pin"
                    size="20px"
                    className="mt-0.5 self-start text-primary-500"
                  />
                  <span>
                    Ronda General Mitre 38-40, Local comercial sótano 1, 08017
                    Barcelona (Junto a Mooby Gran Sarrià Multicines)
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Icon
                    name="basic/phone-call"
                    size="20px"
                    className="text-primary-500"
                  />
                  931 318 618
                </li>
                <li className="flex items-center gap-2">
                  <Icon
                    name="basic/mail"
                    size="20px"
                    className="text-primary-500"
                  />
                  info@gamaoptima.com
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
